import { Component, computed, effect, inject, NgZone, output, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { MapMarker } from '@angular/google-maps';
import { MatDialogRef } from '@angular/material/dialog';
import { EnpsComponent } from '@common/components/base/enps-component';
import { MapViewComponent } from '@common/components/map-view/map-view';
// import { AgmMarker, AgmInfoWindow } from '@agm/core';
import { ObjectTypes } from '@common/enums/object-types';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { NomWebServicesJson } from '@common/services/nom-web-services-json';
import { StFullScreenDialogComponent } from '@common/st-full-screen-dialog/st-full-screen-dialog';
import { Store } from '@ngrx/store';
import { FullscreenLayoutAreas } from '@src/assets/css/fullscreen.template.scss';
import { OpenFullscreenModalActions } from '@src/suite/common/store/fullscreen';
import { ViewStateActions } from '@src/suite/common/store/navigation/actions/viewstate.actions';
import {
    selectCurrentViewState,
    selectViewStateCurrentMapCenter,
    selectViewStateCurrentZoom,
    selectViewStateMapLockMapLocation,
    selectViewStateMapShowTeamLocations,
    selectViewStateMapZoomToCoverage,
} from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { selectAllMarkers } from '@src/suite/common/store/playbook/playbook.selector';
import { RecordHttpActions } from '@src/suite/common/store/records/records.actions';
import { isAllSelectedFilterDefinition } from '@src/suite/common/utils/filter-utils';
// import { selectUserRecordsStateMap } from '@src/suite/common/store/records/records.selectors';
import { AppService } from '@suite/app.service';

import { AvatarBigHorizontalNameComponent } from '../../../common/components/avatar/avatar-big';
import { IconComponent } from '../../../common/components/icon/icon';
import { MapViewComponent as MapViewComponent_1 } from '../../../common/components/map-view/map-view';
import { FooterDirective } from '../../../common/directives/footer-directive';
import { ItemDirective } from '../../../common/directives/item-directive';
import { TopicIconDirective } from '../../../common/directives/topic-icon';

@Component({
    selector: 'st-playbook-map-view',
    imports: [AvatarBigHorizontalNameComponent, FooterDirective, IconComponent, ItemDirective, MapViewComponent_1, TopicIconDirective],
    templateUrl: './playbook-map-view.html',
    styleUrl: './playbook-map-view.scss',
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-playbook-map-view',
    },
})
export class PlaybookMapViewComponent extends EnpsComponent {
    private store = inject(Store);
    public appService = inject(AppService);
    private ngZone = inject(NgZone);
    public nomWebServicesJson = inject(NomWebServicesJson);
    public globalTablesService = inject(GlobalTablesService);

    public readonly mapView = viewChild(MapViewComponent);
    public ObjectTypesEnum = ObjectTypes;
    public readonly LOCKED_CLUSTER_MAX_ZOOM = signal<number>(1);
    public readonly NORMAL_CLUSTER_MAX_ZOOM = signal<number>(10);
    public readonly currentClusterMaxZoom = signal<number>(10);
    public readonly currentUserAuthorized = signal<boolean>(false);
    public dialogRef: MatDialogRef<StFullScreenDialogComponent, any> = null;
    // maps a lat/lng pair to a count value
    public cachedLocationCountsMap: Map<string, number> = new Map<string, number>();
    public cachedUserLocationCountsMap: Map<string, number> = new Map<string, number>();
    public readonly mapUserClick = output<StorytellingRecord>();
    public readonly mapTopicClick = output<StorytellingRecord>();
    public readonly mapAssignmentClick = output<StorytellingRecord>();

    readonly currentVisibleItems = signal<StorytellingRecord[]>([]);
    // both topics and assignments
    currentPopupExtraCount = 0;
    refreshInProgress = false;

    readonly viewStateMapZoomToCoverage = computed(() => this.store.selectSignal(selectViewStateMapZoomToCoverage)());
    readonly viewStateMapShowTeamLocations = computed(() => this.store.selectSignal(selectViewStateMapShowTeamLocations)());
    readonly viewStateMapLockMapLocation = computed(() => this.store.selectSignal(selectViewStateMapLockMapLocation)());
    readonly viewStateCurrentZoom = computed(() => this.store.selectSignal(selectViewStateCurrentZoom)());
    readonly viewStateCurrentMapCenter = computed(() => this.store.selectSignal(selectViewStateCurrentMapCenter)());
    readonly allMarkers = computed(() => this.store.selectSignal(selectAllMarkers)());

    vs = this.store.selectSignal(selectCurrentViewState);

    constructor() {
        super();
        effect(() => {
            if (this.viewStateMapZoomToCoverage()) {
                this.mapView()?.zoomToCoverage();
            }
        });
        effect(() => {
            if (this.viewStateMapShowTeamLocations()) {
                this.refreshMapData();
            }
        });
    }

    //TODO This should be moved in effect
    public async ensureUserResourcesDataLoaded(isFromPoller: boolean) {
        // if someone is explicitly causing the map to rebuild at the same time, we will just skip this poller call
        if (this.refreshInProgress && isFromPoller) {
            return;
        }
        this.currentUserAuthorized.set(this.nomWebServicesJson.HelloResults.Supervisor);
        if (!this.currentUserAuthorized()) {
            // if this is not a user user, check if he has the ViewUserLocation permission
            if (
                this.globalTablesService.securityPrivileges[this.nomWebServicesJson.PSTGroup] &&
                this.globalTablesService.securityPrivileges[this.nomWebServicesJson.PSTGroup].viewUserLocationPrivileges
            ) {
                this.currentUserAuthorized.set(true);
            } else if (this.nomWebServicesJson.HelloResults.SecurityPrivileges.length) {
                for (const privilege of this.nomWebServicesJson.HelloResults.SecurityPrivileges) {
                    if (privilege.GroupId === this.nomWebServicesJson.PSTGroup && privilege.ViewUserLocationPrivileges) {
                        this.currentUserAuthorized.set(true);
                    }
                }
            }
        }
        let fq = '';
        // A.T. encode geo-spatial search SOLR Filter Query (fq). We need a bounding box (rectangle),
        // identified by a lower left (south-west) and a top-right (north-east) corners, which pass through the 180 deg meridian
        // RSLocation is the stored SOLR field of type solr.LatLonPointSpatialField,
        // on which SOLR will be doing the geo - spatial filtering calcs
        const mapView = this.mapView();
        if (mapView.currentMapBounds()) {
            const southWest: google.maps.LatLng = mapView.currentMapBounds().getSouthWest();
            const northEast: google.maps.LatLng = mapView.currentMapBounds().getNorthEast();
            //RSLocation_spf is a special field derived from the regular G_FIELD,
            //which is of type solr.LatLonPointSpatialField
            fq = 'RSLocation_spf:[' + southWest.lat() + ',' + southWest.lng() + ' TO ' + northEast.lat() + ',' + northEast.lng() + ']';
        }
        if (this.currentUserAuthorized()) {
            // A.T. it's best if we just clear and rebuild the resources. there are many edge cases that could be
            // problematic if we try to find and match with an existing user and just update
            // there could be new users, deleted users, the  other props (not location) of an existing user may have changed
            // etc.
            this.appService.userResources = [];
            let userLocations = [];
            const pstLocationFilter = (this.vs() as StorytellingSingleViewStateRecord)?.PSTViewStateFilter?.Filters?.filter(f => f.ID === 'PSTTeamLocation')[0];

            if (pstLocationFilter && !pstLocationFilter.NotUsed) {
                if (isAllSelectedFilterDefinition(pstLocationFilter)) {
                    userLocations = this.globalTablesService.PlaybookTeamsArray.map(t => `P_${t.ID}\\`);
                } else {
                    const v: string[] = pstLocationFilter.Value;
                    userLocations = v.map(t => `P_${t}\\`);
                }
                if (userLocations.length === 0) {
                    userLocations = this.globalTablesService.PlaybookTeamsArray.map(t => `P_${t.ID}\\`);
                }
            } else {
                userLocations.push(`P_${this.nomWebServicesJson.HelloResults.HomeProgram}\\`);
            }
            this.store.dispatch(RecordHttpActions.loadUserLocations({ fq, locations: userLocations }));
            // if (isFromPoller) {
            //     this.rebuildCachedUserLocations();
            // }
        }
    }
    public async refreshMapData(_fitDefaultBounds?: boolean) {
        if (this.refreshInProgress) {
            return;
        }
        this.refreshInProgress = true;
        await this.ensureUserResourcesDataLoaded(false);
        this.refreshInProgress = false;
        // TODO Fix this
        // this.ngZone.runOutsideAngular(() => {
        //     setTimeout(
        //         () => {
        //             // clear existing markers
        //             DataStore.markers = [];
        //             this.ensureUserResourcesDataLoaded(false)
        //                 .then(() => {
        //                     this.rebuildCachedLocations();
        //                     for (const user of this.appService.userResources) {
        //                         this.addUserToMap(user);
        //                     }
        //                     const allAssignments: Array<StorytellingRecord> = [];
        //                     for (const topic of this.playbookService.playbookSearchResults.topics) {
        //                         this.addRecordToMap(topic);
        //                         for (const assignment of topic.getChildren()) {
        //                             allAssignments.push(assignment);
        //                         }
        //                     }
        //                     for (const assignment of allAssignments) {
        //                         this.addRecordToMap(assignment);
        //                     }
        //                     for (const assignment of this.playbookService.playbookSearchResults.assignments) {
        //                         this.addRecordToMap(assignment);
        //                     }
        //                     for (const topic of PlaybookSearchResults.newlyAddedItems.items) {
        //                         this.addRecordToMap(topic);
        //                     }
        //                     if (this.mapView) this.mapView.init(fitDefaultBounds);
        //                     this.refreshInProgress = false;
        //                 })
        //                 .catch(err => {
        //                     this.refreshInProgress = false;
        //                     throw err;
        //                 });
        //         },
        //         1,
        //         this.changeDetectorRef
        //     );
        // });
    }

    public rebuildCachedUserLocations() {
        this.cachedUserLocationCountsMap.clear();
        for (const user of this.appService.userResources) {
            if (user?.RSLocation?.locations?.length > 0) {
                this.addCachedUserLocation(user);
            }
        }
    }

    private rebuildCachedLocations() {
        this.cachedLocationCountsMap.clear();

        // TODO FIX THIS
        // for (const topic of this.playbookService.playbookSearchResults.topics) {
        //     this.addCachedLocation(topic);
        //     for (const assignment of topic.children) {
        //         this.addCachedLocation(assignment);
        //     }
        // }
        // for (const assignment of this.playbookService.playbookSearchResults.assignments) {
        //     this.addCachedLocation(assignment);
        // }
        // for (const topic of PlaybookSearchResults.newlyAddedItems.items) {
        //     this.addCachedLocation(topic);
        //     if (topic instanceof StorytellingTopicRecord) {
        //         for (const assignment of topic.getChildren()) {
        //             this.addCachedLocation(assignment);
        //         }
        //     }
        // }
        // this.rebuildCachedUserLocations();
    }

    private addCachedUserLocation(user: StorytellingUserRecord) {
        if (user?.RSLocation?.locations?.length > 0) {
            const lat = user.RSLocation.getLastKnownLocation().latitude;
            const lng = user.RSLocation.getLastKnownLocation().longitude;
            if (this.cachedUserLocationCountsMap.has(`${lat}_${lng}`)) {
                this.cachedUserLocationCountsMap.set(`${lat}_${lng}`, this.cachedUserLocationCountsMap.get(`${lat}_${lng}`) + 1);
            } else {
                this.cachedUserLocationCountsMap.set(`${lat}_${lng}`, 1);
            }
        }
    }

    private addCachedLocation(rec: StorytellingTopicRecord) {
        if (rec.PSTTopicAddress && rec.PSTTopicAddress.lat !== 0) {
            const lat = Number(rec.PSTTopicAddress.lat);
            const lng = Number(rec.PSTTopicAddress.long);
            if (this.cachedLocationCountsMap.has(`${lat}_${lng}`)) {
                this.cachedLocationCountsMap.set(`${lat}_${lng}`, this.cachedLocationCountsMap.get(`${lat}_${lng}`) + 1);
            } else {
                this.cachedLocationCountsMap.set(`${lat}_${lng}`, 1);
            }
        }
    }

    public updateCurrentClusterZoom(lock: boolean, useMarkers?: boolean) {
        if (lock) {
            this.currentClusterMaxZoom.set(this.LOCKED_CLUSTER_MAX_ZOOM());
        } else {
            this.currentClusterMaxZoom.set(this.NORMAL_CLUSTER_MAX_ZOOM());
        }
        // needed to force refresh otherwise clusters never break down into individual pins with count inside them
        this.refreshMapData(!useMarkers);
    }

    async openDialog(data: StorytellingRecord) {
        if (data.Type === ObjectTypes.TOPIC) {
            this.store.dispatch(OpenFullscreenModalActions.openTopicFullscreenDialog({ recordGuid: data.Guid, recordType: data.Type }));
        } else {
            this.store.dispatch(OpenFullscreenModalActions.openAssignmentFullscreenDialog({ recordGuid: data.Guid, recordType: data.Type, sidecarMenu: FullscreenLayoutAreas.SIDECAR_MAIN }));
        }
    }

    private addRecordOnLocationMatch(_rec: StorytellingRecord, _marker: MapMarker) {
        // TODO FIX THIS
        // if (
        //     ((rec instanceof StorytellingTopicRecord || rec instanceof StorytellingAssignmentRecord) &&
        //         rec.PSTTopicAddress &&
        //         rec.PSTTopicAddress.lat !== 0 &&
        //         marker.getPosition().lat().toString() === rec.PSTTopicAddress.lat &&
        //         marker.getPosition().lng().toString() === rec.PSTTopicAddress.long) ||
        //     (rec instanceof StorytellingUserRecord &&
        //         (rec as StorytellingUserRecord).RSLocation.locations.length > 0 &&
        //         (rec as StorytellingUserRecord).RSLocation.getLastKnownLocation().latitude === marker.getPosition().lat() &&
        //         (rec as StorytellingUserRecord).RSLocation.getLastKnownLocation().longitude === marker.getPosition().lng())
        // ) {
        //     if (this.currentVisibleItems.length < 10) {
        //         this.currentVisibleItems = [...this.currentVisibleItems, rec];
        //     } else {
        //         this.currentPopupExtraCount++;
        //     }
        // }
    }

    public getUserID(rec: StorytellingUserRecord): string {
        return this.appService.getUserIDV2(rec);
    }

    recordClicked(rec: StorytellingRecord) {
        switch (rec.Type) {
            case ObjectTypes.TOPIC: {
                this.openDialog(rec);
                break;
            }
            case ObjectTypes.ASSIGNMENT: {
                this.openDialog(rec);
                break;
            }
            case ObjectTypes.USER: {
                this.mapUserClick.emit(rec);
                break;
            }
        }
    }

    updateCurrentMapCenter($event: any) {
        this.store.dispatch(ViewStateActions.updateViewStateCurrentMapCenter({ center: $event }));
    }

    updateCurrentMapZoom($event: any) {
        this.store.dispatch(ViewStateActions.updateViewStateCurrentMapZoom({ zoom: $event }));
    }
}
